// ============================================================================
// ITEM MASTER — Inventory Core slice 1 (ADR-0058).
// Goods the business buys, stocks and sells: the base unit (stock is held in
// it), packs with their factor to base and weights/sizes, barcodes and other
// identifiers per pack, classification values per active dimension, batch /
// expiry tracking, and this entity's stocking settings (default location,
// reorder / min / max). Items are enterprise-shared (ADR-0028); the stocking
// settings belong to the entity in the header (X-Entity-ID).
// Domain-module extension payloads are shown read-only and never sent — except
// TAX (slice 3, ADR-0058 §7): the item's tax mapping under the selected entity's
// jurisdiction, edited on a real Tax tab with the same master picker and
// jurisdiction field schema (target SERVICE) as the Service Master, and sent as
// `extensions.TAX` so it saves inside the item's own transaction.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, useColumnVisibility, type ColumnDefinition,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import { useItems, useUoms } from 'hooks/api/useInventory';
import {
  itemService, itemCategoryService, classificationService, stockLocationService,
  taxMetaService, entityTaxConfigService, taxClassificationService,
} from 'api/services';
import taxMasterService from 'api/services/taxMasterService';
import TaxMasterPicker from 'components/shared/tax-master-module/TaxMasterPicker';
import TaxFieldsPanel, { validateTaxFields, type AsyncOptionSource, type TaxFieldValues } from 'components/modules/tax/TaxFieldsPanel';
import type {
  TaxFieldContextDto, ServiceTaxMappingDto, EntityTaxConfigDto, JurisdictionFieldSchemaDto,
} from 'api/types';
import {
  IDENTIFIER_TYPES,
  type ItemDto, type ItemType, type IdentifierType, type ItemQueryParams,
  type CreateItemRequest, type ItemUomInput, type ItemIdentifierInput,
  type ClassificationDimensionDto, type ClassificationValueDto,
} from 'api/types/inventory.types';
import type { UomDto } from 'api/types/uom.types';

// ============================================================================
// SHARED HELPERS
// ============================================================================

/** Value label with its hierarchy parent when present: "Fruits / Apple". */
const classValueLabel = (v: ClassificationValueDto) => (v.parentName ? `${v.parentName} / ${v.valueName}` : v.valueName);

/**
 * Server order, except a dependent classification (Sub-category) is moved to sit
 * right after the classification it depends on (Category). A parent missing from
 * the list (inactive) leaves its dependents where they are.
 */
const orderDimsByDependency = (list: ClassificationDimensionDto[]): ClassificationDimensionDto[] => {
  const ids = new Set(list.map(d => d.id));
  const placed = new Set<string>();
  const out: ClassificationDimensionDto[] = [];
  const place = (d: ClassificationDimensionDto) => {
    if (placed.has(d.id)) return;
    placed.add(d.id);
    out.push(d);
    list.filter(c => c.dependsOnDimensionId === d.id).forEach(place);
  };
  list.forEach(d => {
    if (!d.dependsOnDimensionId || !ids.has(d.dependsOnDimensionId)) place(d);
  });
  list.forEach(place); // anything left (a dependency loop) keeps server order
  return out;
};

type FormMode = 'create' | 'edit' | 'copy';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const TYPE_LABEL: Record<ItemType, string> = { STOCK: 'Stock', NON_STOCK: 'Non-stock' };

const IDENTIFIER_OPTIONS: SelectOption[] = IDENTIFIER_TYPES.map(t => ({ value: t.value, label: t.label }));
const identifierLabel = (t: IdentifierType) => IDENTIFIER_TYPES.find(x => x.value === t)?.label ?? t;

interface ApiErrorShape { response?: { status?: number; data?: { message?: string; errors?: string[] } } }
const apiError = (err: unknown) => {
  const e = err as ApiErrorShape | null;
  const data = e?.response?.data;
  const errors = Array.isArray(data?.errors) ? data?.errors ?? [] : [];
  return { status: e?.response?.status, message: data?.message || (errors.length ? errors.join(' ') : undefined) };
};

type IconComponent = React.ComponentType<{ size?: number }>;
const ICONS = IconSystem as unknown as Record<string, IconComponent | undefined>;

/** '' → null; otherwise the number (NaN when not numeric — callers validate). */
const toNum = (s: string): number | null => (s.trim() === '' ? null : Number(s));
const numStr = (n?: number | null) => (n == null ? '' : String(n));

const isBarcodeType = (t: IdentifierType) => t === 'GTIN' || t === 'EAN' || t === 'UPC';

/** GS1 check: digits only, length 8/12/13/14, weights 3,1,3,1… from the digit left of the check digit. */
const barcodeError = (value: string): string | null => {
  if (!/^\d+$/.test(value) || ![8, 12, 13, 14].includes(value.length)) {
    return 'A barcode is 8, 12, 13 or 14 digits';
  }
  const body = value.slice(0, -1);
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    sum += Number(body[body.length - 1 - i]) * (i % 2 === 0 ? 3 : 1);
  }
  const check = (10 - (sum % 10)) % 10;
  return check === Number(value[value.length - 1]) ? null : 'Not a valid barcode (check digit)';
};

const uomLabel = (u: UomDto) => `${u.uomCode} — ${u.uomName}`;

// Extension codes are a deployment fact: read once per session.
let extensionsPromise: Promise<string[]> | null = null;
const loadEnabledExtensions = (): Promise<string[]> => {
  if (!extensionsPromise) {
    extensionsPromise = itemService.getEnabledExtensions().catch(() => { extensionsPromise = null; return []; });
  }
  return extensionsPromise;
};

// ---- Tax (extension code TAX) ---------------------------------------------
const TAX_EXT = 'TAX';
const CLASSIFICATION_BINDING = 'LINE.SAC_HSN_CODE';
const UNIT_BINDING = 'LINE.UNIT';
const CLASSIFICATION_SOURCE = 'TAX.CLASSIFICATION';
const bindingOf = (f: JurisdictionFieldSchemaDto) => (f.binding || '').toUpperCase();

/** Race a promise against a timeout so a slow backend can't lock the modal. */
const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([p, new Promise<T>(res => setTimeout(() => res(fallback), ms))]);

/**
 * The selected entity's tax setup for items: the SERVICE field context (jurisdiction, field
 * schema, units — items share the service schema) and the entity's defaults (default master,
 * classification enforcement). Cached per entity; the screen clears it on mount so a change
 * under Tax Settings shows on the next visit. ctx null = could not be read.
 */
interface TaxSetup { ctx: TaxFieldContextDto | null; defaults: EntityTaxConfigDto | null }
const taxSetupCache = new Map<string, Promise<TaxSetup>>();
const invalidateTaxSetup = () => taxSetupCache.clear();
const loadTaxSetup = (entityKey: string): Promise<TaxSetup> => {
  let p = taxSetupCache.get(entityKey);
  if (!p) {
    p = Promise.all([
      withTimeout(taxMetaService.getFieldContext('SERVICE').catch(() => null), 6000, null),
      withTimeout(entityTaxConfigService.get().catch(() => null), 6000, null),
    ]).then(([ctx, defaults]) => {
      if (!ctx) taxSetupCache.delete(entityKey); // retry next time
      return { ctx, defaults };
    });
    taxSetupCache.set(entityKey, p);
  }
  return p;
};

/** The item's tax mapping as edited: the master plus the jurisdiction's field values. */
interface TaxForm {
  rateCode: string;
  rateName: string;
  /** Of the loaded mapping; 0 = none saved (insert). */
  rowVersion: number;
  values: TaxFieldValues;
}
const emptyTax = (): TaxForm => ({ rateCode: '', rateName: '', rowVersion: 0, values: {} });
const asMapping = (v: unknown): ServiceTaxMappingDto | null =>
  (v && typeof v === 'object' ? (v as ServiceTaxMappingDto) : null);
const mappingToTax = (m: ServiceTaxMappingDto | null, keepVersion: boolean): TaxForm => (m
  ? { rateCode: m.rateCode || '', rateName: m.rateName || '', rowVersion: keepVersion ? m.rowVersion || 0 : 0, values: { ...(m.attributes || {}) } }
  : emptyTax());
const textValue = (v: unknown) => (v == null ? '' : String(v).trim());

const Spinner: React.FC<{ label: string }> = ({ label }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '0.75rem', zIndex: 10, borderRadius: 'inherit',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const sectionTitle: React.CSSProperties = {
  fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-secondary)', margin: '0.25rem 0 0.6rem',
};
const hintStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' };
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.45rem 0.5rem', fontWeight: 600, color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top', color: 'var(--text-primary)',
};
const cellInput: React.CSSProperties = { padding: '0.3rem 0.5rem', fontSize: '0.8125rem' };
const dangerBadge: React.CSSProperties = { background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' };
const mutedBadge: React.CSSProperties = { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' };

const TabBar: React.FC<{
  tabs: { id: string; label: string; hasError?: boolean }[];
  active: string;
  onChange: (id: string) => void;
}> = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid var(--border-color)', padding: '0 1rem', flexShrink: 0 }}>
    {tabs.map(t => (
      <button key={t.id} type="button" onClick={() => onChange(t.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.55rem 0.9rem', fontSize: '0.85rem',
          color: active === t.id ? 'var(--primary)' : 'var(--text-primary)', fontWeight: active === t.id ? 600 : 500,
          borderBottom: `2px solid ${active === t.id ? 'var(--primary)' : 'transparent'}`, marginBottom: -1,
        }}>
        {t.label}
        {t.hasError && (
          <span title="Has errors" style={{
            display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
            background: 'var(--danger, #ef4444)', marginLeft: 6, verticalAlign: 'middle',
          }} />
        )}
      </button>
    ))}
  </div>
);

// ============================================================================
// FORM STATE
// ============================================================================

const BASE_KEY = 'base';

interface PackRow {
  key: string;
  /** Ignored for the base row: it follows FormState.baseUomId. */
  uomId: string;
  factor: string;
  isPurchaseDefault: boolean;
  isSalesDefault: boolean;
  netWeight: string;
  grossWeight: string;
  weightUomId: string;
  length: string;
  width: string;
  height: string;
  dimensionUomId: string;
}

interface IdentifierRow {
  key: string;
  identifierType: IdentifierType;
  identifierValue: string;
  /** The pack row it scans to; BASE_KEY = the base unit. */
  packKey: string;
  isPrimary: boolean;
}

interface FormState {
  itemCode: string;
  itemName: string;
  description: string;
  itemType: ItemType;
  categoryId: string;
  baseUomId: string;
  statusCode: string;
  blockedForPurchase: boolean;
  blockedForSale: boolean;
  packs: PackRow[];
  identifiers: IdentifierRow[];
  /** dimensionId → valueId. */
  classifications: Record<string, string>;
  trackBatch: boolean;
  trackExpiry: boolean;
  shelfLifeDays: string;
  defaultStockLocationId: string;
  reorderLevel: string;
  minStock: string;
  maxStock: string;
  /** Tax tab (extension TAX) — the mapping under the selected entity's jurisdiction. */
  tax: TaxForm;
}

const emptyPack = (key: string, factor = ''): PackRow => ({
  key, uomId: '', factor, isPurchaseDefault: false, isSalesDefault: false,
  netWeight: '', grossWeight: '', weightUomId: '', length: '', width: '', height: '', dimensionUomId: '',
});

const emptyForm = (): FormState => ({
  itemCode: '', itemName: '', description: '', itemType: 'STOCK', categoryId: '', baseUomId: '',
  statusCode: 'ACTIVE', blockedForPurchase: false, blockedForSale: false,
  packs: [emptyPack(BASE_KEY, '1')], identifiers: [], classifications: {},
  trackBatch: false, trackExpiry: false, shelfLifeDays: '',
  defaultStockLocationId: '', reorderLevel: '', minStock: '', maxStock: '',
  tax: emptyTax(),
});

const buildForm = (item: ItemDto, mode: FormMode, nextKey: () => string): FormState => {
  const uoms = [...(item.uoms || [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const base = uoms.find(u => u.isBase || u.uomId === item.baseUomId);
  const toRow = (u: (typeof uoms)[number], key: string): PackRow => ({
    key, uomId: u.uomId, factor: key === BASE_KEY ? '1' : numStr(u.factorToBase),
    isPurchaseDefault: u.isPurchaseDefault, isSalesDefault: u.isSalesDefault,
    netWeight: numStr(u.netWeight), grossWeight: numStr(u.grossWeight), weightUomId: u.weightUomId || '',
    length: numStr(u.length), width: numStr(u.width), height: numStr(u.height), dimensionUomId: u.dimensionUomId || '',
  });
  const uomToKey = new Map<string, string>();
  const packs: PackRow[] = [base ? toRow(base, BASE_KEY) : emptyPack(BASE_KEY, '1')];
  uomToKey.set(item.baseUomId, BASE_KEY);
  uoms.filter(u => u !== base).forEach(u => {
    const key = nextKey();
    uomToKey.set(u.uomId, key);
    packs.push(toRow(u, key));
  });
  const identifiers: IdentifierRow[] = mode === 'copy' ? [] : (item.identifiers || []).map(i => ({
    key: nextKey(),
    identifierType: i.identifierType,
    identifierValue: i.identifierValue,
    packKey: (i.uomId && uomToKey.get(i.uomId)) || BASE_KEY,
    isPrimary: i.isPrimary,
  }));
  const classifications: Record<string, string> = {};
  (item.classifications || []).forEach(c => { classifications[c.dimensionId] = c.valueId; });
  const es = item.entitySetting;
  return {
    itemCode: mode === 'copy' ? '' : item.itemCode,
    itemName: item.itemName,
    description: item.description || '',
    itemType: item.itemType,
    categoryId: item.categoryId || '',
    baseUomId: item.baseUomId,
    statusCode: item.statusCode || 'ACTIVE',
    blockedForPurchase: item.blockedForPurchase,
    blockedForSale: item.blockedForSale,
    packs, identifiers, classifications,
    trackBatch: item.trackBatch,
    trackExpiry: item.trackExpiry,
    shelfLifeDays: numStr(item.shelfLifeDays),
    defaultStockLocationId: es?.defaultStockLocationId || '',
    reorderLevel: numStr(es?.reorderLevel),
    minStock: numStr(es?.minStock),
    maxStock: numStr(es?.maxStock),
    tax: emptyTax(), // set by the loader once the jurisdiction is known
  };
};

const packUomId = (form: FormState, p: PackRow) => (p.key === BASE_KEY ? form.baseUomId : p.uomId);
const hasWeight = (p: PackRow) => [p.netWeight, p.grossWeight].some(v => v.trim() !== '');
const hasSize = (p: PackRow) => [p.length, p.width, p.height].some(v => v.trim() !== '');

/** Live issues on the identifier rows: barcode check digit and duplicates. */
const identifierIssues = (rows: IdentifierRow[]): Record<string, string> => {
  const issues: Record<string, string> = {};
  const seen = new Map<string, string>();
  rows.forEach(r => {
    const v = r.identifierValue.trim();
    if (!v) return;
    if (isBarcodeType(r.identifierType)) {
      const e = barcodeError(v);
      if (e) { issues[r.key] = e; return; }
    }
    const k = v.toUpperCase();
    if (seen.has(k)) issues[r.key] = 'This value is already in the list';
    else seen.set(k, r.key);
  });
  return issues;
};

type TabId = string; // 'general' | 'packs' | 'class' | 'stock' | `ext:${code}`

const tabOfError = (key: string): TabId => {
  if (key.startsWith('pack.') || key.startsWith('ident.')) return 'packs';
  if (key.startsWith('class.')) return 'class';
  if (['shelfLifeDays', 'reorderLevel', 'minStock', 'maxStock'].includes(key)) return 'stock';
  if (key.startsWith('tax.')) return `ext:${TAX_EXT}`;
  return 'general';
};

const validateForm = (form: FormState, dims: ClassificationDimensionDto[]): Record<string, string> => {
  const e: Record<string, string> = {};
  if (!form.itemCode.trim()) e.itemCode = 'Item code is required';
  if (!form.itemName.trim()) e.itemName = 'Item name is required';
  if (!form.baseUomId) e.baseUomId = 'Choose the base unit';

  const used = new Map<string, string>();
  form.packs.forEach(p => {
    const uid = packUomId(form, p);
    if (p.key !== BASE_KEY) {
      if (!uid) e[`pack.${p.key}.uom`] = 'Choose a unit';
      const f = toNum(p.factor);
      if (f == null || !Number.isFinite(f) || f <= 0) e[`pack.${p.key}.factor`] = 'Enter how many base units it contains (> 0)';
    }
    if (uid) {
      if (used.has(uid)) e[`pack.${p.key}.uom`] = 'This unit is already used by another pack';
      else used.set(uid, p.key);
    }
    const weights = [p.netWeight, p.grossWeight].map(toNum);
    if (weights.some(w => w != null && (!Number.isFinite(w) || w < 0))) e[`pack.${p.key}.weight`] = 'Weights must be numbers ≥ 0';
    else if (hasWeight(p) && !p.weightUomId) e[`pack.${p.key}.weight`] = 'Choose the weight unit';
    const sizes = [p.length, p.width, p.height].map(toNum);
    if (sizes.some(s => s != null && (!Number.isFinite(s) || s < 0))) e[`pack.${p.key}.size`] = 'Sizes must be numbers ≥ 0';
    else if (hasSize(p) && !p.dimensionUomId) e[`pack.${p.key}.size`] = 'Choose the size unit';
  });

  const issues = identifierIssues(form.identifiers);
  form.identifiers.forEach(r => {
    if (!r.identifierValue.trim()) { e[`ident.${r.key}`] = 'Enter the value'; return; }
    if (issues[r.key]) { e[`ident.${r.key}`] = issues[r.key]; return; }
    if (r.packKey !== BASE_KEY) {
      const pack = form.packs.find(p => p.key === r.packKey);
      if (!pack?.uomId) e[`ident.${r.key}`] = 'The pack it points at has no unit yet';
    }
  });

  if (form.itemType === 'STOCK') {
    dims.filter(d => d.isRequired).forEach(d => {
      if (!form.classifications[d.id]) e[`class.${d.id}`] = `${d.dimensionName} is required for stock items`;
    });
  }

  const nonNeg = (field: keyof FormState, label: string, integer = false) => {
    const raw = form[field];
    if (typeof raw !== 'string') return;
    const n = toNum(raw);
    if (n == null) return;
    if (!Number.isFinite(n) || n < 0 || (integer && !Number.isInteger(n))) e[field] = `${label} must be ${integer ? 'a whole number' : 'a number'} ≥ 0`;
  };
  nonNeg('shelfLifeDays', 'Shelf life', true);
  nonNeg('reorderLevel', 'Reorder level');
  nonNeg('minStock', 'Minimum');
  nonNeg('maxStock', 'Maximum');
  const min = toNum(form.minStock);
  const max = toNum(form.maxStock);
  if (!e.minStock && !e.maxStock && min != null && max != null && min > max) e.maxStock = 'Maximum must be at least the minimum';
  return e;
};

const buildRequest = (form: FormState): CreateItemRequest => {
  const uoms: ItemUomInput[] = form.packs.map((p, i) => {
    const w = hasWeight(p);
    const s = hasSize(p);
    return {
      uomId: packUomId(form, p),
      factorToBase: p.key === BASE_KEY ? 1 : Number(p.factor),
      isPurchaseDefault: p.isPurchaseDefault,
      isSalesDefault: p.isSalesDefault,
      netWeight: toNum(p.netWeight),
      grossWeight: toNum(p.grossWeight),
      weightUomId: w ? p.weightUomId || null : null,
      length: toNum(p.length),
      width: toNum(p.width),
      height: toNum(p.height),
      dimensionUomId: s ? p.dimensionUomId || null : null,
      displayOrder: i,
    };
  });
  const identifiers: ItemIdentifierInput[] = form.identifiers.map(r => ({
    uomId: r.packKey === BASE_KEY ? null : (form.packs.find(p => p.key === r.packKey)?.uomId || null),
    identifierType: r.identifierType,
    identifierValue: r.identifierValue.trim(),
    isPrimary: r.isPrimary,
  }));
  return {
    itemCode: form.itemCode.trim(),
    itemName: form.itemName.trim(),
    description: form.description.trim() || null,
    itemType: form.itemType,
    baseUomId: form.baseUomId,
    categoryId: form.categoryId || null,
    trackBatch: form.trackBatch,
    trackExpiry: form.trackBatch && form.trackExpiry,
    shelfLifeDays: toNum(form.shelfLifeDays),
    blockedForPurchase: form.blockedForPurchase,
    blockedForSale: form.blockedForSale,
    statusCode: form.statusCode,
    uoms,
    identifiers,
    classifications: Object.entries(form.classifications)
      .filter(([, valueId]) => !!valueId)
      .map(([dimensionId, valueId]) => ({ dimensionId, valueId })),
    entitySetting: {
      defaultStockLocationId: form.defaultStockLocationId || null,
      reorderLevel: toNum(form.reorderLevel),
      minStock: toNum(form.minStock),
      maxStock: toNum(form.maxStock),
    },
  };
};

// ============================================================================
// FORM MODAL
// ============================================================================

interface ItemFormModalProps {
  isOpen: boolean;
  mode: FormMode;
  record?: ItemDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: FormMode, instanceId?: string, recordCode?: string) => void;
}

const ItemFormModal: React.FC<ItemFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, copyInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const { selectedEntity } = useAuthContext();
  const { data: uoms } = useUoms();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [loaded, setLoaded] = useState<ItemDto | null>(null);
  const [dims, setDims] = useState<ClassificationDimensionDto[]>([]);
  const [extensions, setExtensions] = useState<string[]>([]);
  const [tab, setTab] = useState<TabId>('general');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const snapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const loadSeqRef = useRef(0);
  const keySeqRef = useRef(0);
  const nextKey = useCallback(() => `r${++keySeqRef.current}`, []);

  const isOpenRef = useRef(isOpen); isOpenRef.current = isOpen;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId); copyInstanceIdRef.current = copyInstanceId;
  const formRef = useRef(form); formRef.current = form;
  const loadedRef = useRef(loaded); loadedRef.current = loaded;
  const tabRef = useRef(tab); tabRef.current = tab;

  // Label caches for the async pickers (seedOptions resolve the saved value's label).
  const categoryLabels = useRef<Map<string, string>>(new Map());
  const locationLabels = useRef<Map<string, string>>(new Map());
  const valueLabels = useRef<Map<string, string>>(new Map());
  // Value rows seen in the pickers, by id — their allowedUnder decides whether a
  // dependent value survives a change of its parent value.
  const valueRows = useRef<Map<string, ClassificationValueDto>>(new Map());
  // Per-dimension inline note after a dependent value was cleared.
  const [classNotes, setClassNotes] = useState<Record<string, string>>({});

  // Tax tab: the entity's tax setup, the master picker, and the classification filter.
  const [taxSetup, setTaxSetup] = useState<TaxSetup | null>(null);
  const [taxPickerOpen, setTaxPickerOpen] = useState(false);
  const [goodsOnly, setGoodsOnly] = useState(true);
  // Classification code → "CODE — description" (seed label) and the unit the master suggests.
  const classificationCache = useRef<Map<string, { label: string; defaultUnitCode?: string | null }>>(new Map());
  const entityKey = selectedEntity?.id || '';

  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setErrors({}); return; }
    const m = modeRef.current;
    const src = m !== 'create' ? recordRef.current : null;
    const seq = ++loadSeqRef.current;
    snapshotRef.current = '';
    setForm(emptyForm()); setLoaded(null); setTab('general'); setExpanded(new Set()); setErrors({});
    setClassNotes({}); setTaxPickerOpen(false); setGoodsOnly(true);
    setLoadingData(true);
    (async () => {
      try {
        const [full, activeDims, ext, setup] = await Promise.all([
          src ? itemService.getById(src.id) : Promise.resolve(null),
          classificationService.getActive().catch(() => [] as ClassificationDimensionDto[]),
          loadEnabledExtensions(),
          loadTaxSetup(entityKey),
        ]);
        if (seq !== loadSeqRef.current) return;
        let f = emptyForm();
        if (full) {
          if (full.categoryId) categoryLabels.current.set(full.categoryId, full.categoryName || full.categoryCode || '');
          const es = full.entitySetting;
          if (es?.defaultStockLocationId) {
            locationLabels.current.set(es.defaultStockLocationId,
              es.defaultStockLocationCode ? `${es.defaultStockLocationCode} — ${es.defaultStockLocationName || ''}` : es.defaultStockLocationName || '');
          }
          (full.classifications || []).forEach(c => valueLabels.current.set(c.valueId, c.valueName || c.valueCode || ''));
          f = buildForm(full, m, nextKey);
          // Edit keeps the mapping's rowVersion; a copy starts a NEW mapping row (0).
          const mapping = setup.ctx?.jurisdiction ? asMapping(full.extensions?.[TAX_EXT]) : null;
          f.tax = mappingToTax(mapping, m === 'edit');
          if (mapping?.classificationCode) {
            classificationCache.current.set(mapping.classificationCode.toUpperCase(), {
              label: mapping.classificationDescription
                ? `${mapping.classificationCode} — ${mapping.classificationDescription}`
                : mapping.classificationCode,
            });
          }
        }
        setTaxSetup(setup);
        setDims(orderDimsByDependency([...activeDims].sort((a, b) => a.displayOrder - b.displayOrder)));
        setExtensions(ext);
        setLoaded(full);
        setForm(f);
        snapshotRef.current = JSON.stringify(f);
      } catch (err) {
        if (seq !== loadSeqRef.current) return;
        notificationService.toast.error(apiError(err).message || 'Could not load the item.');
        onClose();
      } finally {
        if (seq === loadSeqRef.current) setLoadingData(false);
      }
    })();
    // mode/record/onClose are read at open time on purpose (refs + Pattern B).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(
    () => (snapshotRef.current ? JSON.stringify(form) !== snapshotRef.current : false),
    [form],
  );

  const upd = useCallback((patch: Partial<FormState>) => setForm(p => ({ ...p, ...patch })), []);
  const clearErr = useCallback((...keys: string[]) => setErrors(p => {
    if (!keys.some(k => p[k])) return p;
    const n = { ...p };
    keys.forEach(k => delete n[k]);
    return n;
  }), []);
  const handleClose = useCallback(() => { if (isDirty) setDiscardOpen(true); else onClose(); }, [isDirty, onClose]);

  // ---- Minimize ----------------------------------------------------------
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentMode = modeRef.current;
    const savedForm = formRef.current;
    const savedLoaded = loadedRef.current;
    const savedTab = tabRef.current;
    const savedSnapshot = snapshotRef.current;
    const dirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;
    const createIdx = createInstanceIdRef.current || '1';
    const copyIdx = copyInstanceIdRef.current || '1';
    const recordCode = savedLoaded?.itemCode || recordRef.current?.itemCode || '';
    const label = savedForm.itemCode.trim() || savedForm.itemName.trim();

    let modalId: string;
    let title: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${createIdx}`;
      title = label ? `Add - ${label} (${createIdx})` : `Add New (${createIdx})`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${copyIdx}`;
      title = label ? `Copy - ${label} (${copyIdx})` : `Copy (${copyIdx})`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordCode}`;
      title = `Edit - ${recordCode || 'Item'}`;
    }

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: dirty,
      onRestore: () => {
        // Only skip the open-effect when it will actually fire (modal currently closed);
        // otherwise the flag would leak into the next fresh open and skip its load.
        if (!isOpenRef.current) isRestoreRef.current = true;
        loadSeqRef.current++; // drop any in-flight load for another session
        setLoadingData(false);
        setForm(savedForm); setLoaded(savedLoaded); setTab(savedTab);
        snapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? createIdx : currentMode === 'copy' ? copyIdx : undefined,
          currentMode === 'edit' ? recordCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  useEffect(() => {
    if (minimizeRef) minimizeRef.current = handleMinimize;
  }, [minimizeRef, handleMinimize]);

  // ---- Async pickers -----------------------------------------------------
  const loadCategories = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemCategoryService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(c => {
        const label = c.path ?? c.categoryName;
        categoryLabels.current.set(c.id, label);
        return { value: c.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadLocations = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await stockLocationService.lookup({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(l => {
        const label = `${l.locationCode} — ${l.locationName}`;
        locationLabels.current.set(l.id, label);
        return { value: l.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const dimIds = useMemo(() => new Set(dims.map(d => d.id)), [dims]);
  /** The parent dimension a dependent picker filters by — only when that parent is on this form. */
  const parentDimOf = useCallback(
    (d: ClassificationDimensionDto) => (d.dependsOnDimensionId && dimIds.has(d.dependsOnDimensionId) ? d.dependsOnDimensionId : null),
    [dimIds],
  );

  // Loaders read the parent's current value from formRef; the picker is keyed by
  // that value so it remounts (and re-queries) whenever the parent changes.
  const valueLoaders = useMemo(() => {
    const map: Record<string, (input: string) => Promise<SelectOption[]>> = {};
    dims.forEach(d => {
      const parentDimId = parentDimOf(d);
      map[d.id] = async (input: string) => {
        const parentValueId = parentDimId ? formRef.current.classifications[parentDimId] : undefined;
        if (parentDimId && !parentValueId) return [];
        try {
          const res = await classificationService.getValues({
            dimensionId: d.id, searchTerm: input || undefined, pageSize: 50,
            leavesOnly: d.leafOnly || undefined,
            allowedUnderValueId: parentValueId || undefined,
          });
          return res.data.map(v => {
            const label = classValueLabel(v);
            valueLabels.current.set(v.id, label);
            valueRows.current.set(v.id, v);
            return { value: v.id, label };
          });
        } catch {
          return [];
        }
      };
    });
    return map;
  }, [dims, parentDimOf]);

  /**
   * Set (or clear, valueId '') one dimension's value, then walk its dependents:
   * a chosen dependent value whose allowedUnder list is non-empty and no longer
   * includes the new parent value is cleared (and so on down the chain). A value
   * not seen in a picker (loaded with the item) is kept — the server validates it.
   */
  const setClassValue = useCallback((dimId: string, valueId: string) => {
    const next = { ...formRef.current.classifications };
    if (valueId) next[dimId] = valueId; else delete next[dimId];
    const notes: Record<string, string> = {};
    const cascade = (parentDimId: string) => {
      const parentValueId = next[parentDimId] || '';
      dims.filter(c => c.dependsOnDimensionId === parentDimId).forEach(c => {
        const current = next[c.id];
        if (!current) return;
        const allowed = valueRows.current.get(current)?.allowedUnder || [];
        if (allowed.length === 0) return;
        if (parentValueId && allowed.some(a => a.valueId === parentValueId)) return;
        delete next[c.id];
        const parentDim = dims.find(x => x.id === parentDimId);
        notes[c.id] = parentValueId
          ? `Cleared: not allowed under ${valueLabels.current.get(parentValueId) || 'the new value'}`
          : `Cleared: ${parentDim?.dimensionName || 'its parent'} was cleared`;
        cascade(c.id);
      });
    };
    cascade(dimId);
    setForm(p => ({ ...p, classifications: next }));
    setClassNotes(p => {
      const n = { ...p, ...notes };
      if (!notes[dimId]) delete n[dimId];
      return n;
    });
  }, [dims]);

  // ---- Tax ---------------------------------------------------------------
  const taxCtx = taxSetup?.ctx ?? null;
  const jurisdiction = taxCtx?.jurisdiction ?? null;
  const taxEnabled = extensions.includes(TAX_EXT);
  const classificationField = useMemo(
    () => taxCtx?.fields.find(f => bindingOf(f) === CLASSIFICATION_BINDING) || null, [taxCtx]);
  const unitField = useMemo(
    () => taxCtx?.fields.find(f => bindingOf(f) === UNIT_BINDING) || null, [taxCtx]);

  const updTaxValue = useCallback((key: string, val: unknown) => {
    clearErr(`tax.${key}`);
    setForm(p => {
      const values = { ...p.tax.values, [key]: val };
      // Picking a classification the master knows suggests its default unit when none is chosen.
      if (classificationField && key === classificationField.fieldKey && unitField && typeof val === 'string' && val) {
        const known = classificationCache.current.get(val.toUpperCase());
        if (known?.defaultUnitCode && !values[unitField.fieldKey]) values[unitField.fieldKey] = known.defaultUnitCode;
      }
      return { ...p, tax: { ...p.tax, values } };
    });
  }, [classificationField, unitField, clearErr]);

  // TAX.CLASSIFICATION: searched through the classification master — GOODS codes (HSN) by
  // default for an item, all codes on "Show all"; free entry unless the entity enforces it.
  const classificationEnforced = !!taxCtx?.classificationEnforced;
  const asyncSources = useMemo<Record<string, AsyncOptionSource>>(() => ({
    [CLASSIFICATION_SOURCE]: {
      load: async (input: string) => {
        try {
          const res = await taxClassificationService.getAll({
            searchTerm: input || undefined, pageSize: 25,
            asOf: new Date().toISOString().slice(0, 10),
            codeType: goodsOnly ? 'GOODS' : undefined,
          });
          return res.data.map(r => {
            const label = `${r.code} — ${r.description}`;
            classificationCache.current.set(r.code.toUpperCase(), { label, defaultUnitCode: r.defaultUnitCode });
            return { value: r.code, label };
          });
        } catch {
          return [];
        }
      },
      labelFor: (value: string) => classificationCache.current.get(value.toUpperCase())?.label,
      allowFreeEntry: !classificationEnforced,
    },
  }), [goodsOnly, classificationEnforced]);

  /** extensions.TAX — sent whenever the tab is live, even without a master (the server then
   *  keeps an existing mapping, or gives a new one the entity's default master). */
  const buildTaxPayload = (t: TaxForm) => ({
    rateCode: t.rateCode || null,
    classificationCode: classificationField ? textValue(t.values[classificationField.fieldKey]) || null : null,
    unitCode: unitField ? textValue(t.values[unitField.fieldKey]) || null : null,
    attributes: t.values,
    rowVersion: t.rowVersion,
  });

  // ---- Units -------------------------------------------------------------
  const uomById = useMemo(() => new Map(uoms.map(u => [u.id, u])), [uoms]);
  const uomOptions = useMemo(() => uoms.map(u => ({ value: u.id, label: uomLabel(u) })), [uoms]);
  const weightOptions = useMemo(() => uoms.filter(u => u.category === 'WEIGHT').map(u => ({ value: u.id, label: uomLabel(u) })), [uoms]);
  const lengthOptions = useMemo(() => uoms.filter(u => u.category === 'LENGTH').map(u => ({ value: u.id, label: uomLabel(u) })), [uoms]);
  const baseCode = uomById.get(form.baseUomId)?.uomCode || 'base unit';

  // ---- Pack + identifier editing ----------------------------------------
  const updPack = (key: string, patch: Partial<PackRow>) =>
    setForm(p => ({ ...p, packs: p.packs.map(r => (r.key === key ? { ...r, ...patch } : r)) }));

  const setDefault = (key: string, field: 'isPurchaseDefault' | 'isSalesDefault') =>
    setForm(p => {
      const current = p.packs.find(r => r.key === key)?.[field];
      return { ...p, packs: p.packs.map(r => ({ ...r, [field]: r.key === key ? !current : false })) };
    });

  const addPack = () => {
    const key = nextKey();
    setForm(p => ({ ...p, packs: [...p.packs, emptyPack(key)] }));
  };

  const removePack = (key: string) => {
    const affected = form.identifiers.filter(i => i.packKey === key).length;
    setForm(p => ({
      ...p,
      packs: p.packs.filter(r => r.key !== key),
      identifiers: p.identifiers.map(i => (i.packKey === key ? { ...i, packKey: BASE_KEY } : i)),
    }));
    setExpanded(s => { const n = new Set(s); n.delete(key); return n; });
    clearErr(`pack.${key}.uom`, `pack.${key}.factor`, `pack.${key}.weight`, `pack.${key}.size`);
    if (affected > 0) {
      notificationService.toast.warning(`${affected} identifier${affected === 1 ? '' : 's'} pointed at the removed pack and now scan${affected === 1 ? 's' : ''} to the base unit — check them before saving.`);
    }
  };

  const toggleExpanded = (key: string) =>
    setExpanded(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const updIdent = (key: string, patch: Partial<IdentifierRow>) => {
    clearErr(`ident.${key}`);
    setForm(p => ({ ...p, identifiers: p.identifiers.map(r => (r.key === key ? { ...r, ...patch } : r)) }));
  };
  const addIdent = () => {
    const key = nextKey();
    setForm(p => ({
      ...p,
      identifiers: [...p.identifiers, { key, identifierType: 'EAN', identifierValue: '', packKey: BASE_KEY, isPrimary: p.identifiers.length === 0 }],
    }));
  };
  const removeIdent = (key: string) => {
    clearErr(`ident.${key}`);
    setForm(p => ({ ...p, identifiers: p.identifiers.filter(r => r.key !== key) }));
  };

  const liveIdentIssues = useMemo(() => identifierIssues(form.identifiers), [form.identifiers]);

  const packOptions: SelectOption[] = useMemo(() => form.packs.map(p => {
    const uid = packUomId(form, p);
    const code = uomById.get(uid)?.uomCode;
    return {
      value: p.key,
      label: p.key === BASE_KEY ? `${code || 'Base unit'} (base)` : code ? `${code} ×${p.factor || '?'}` : 'Pack without a unit',
    };
  }), [form, uomById]);

  // ---- Save --------------------------------------------------------------
  const handleSave = async () => {
    const e = validateForm(form, dims);
    // Tax: the jurisdiction's field rules apply once a master is chosen. No master is not an
    // error — the server applies the entity's default master or leaves the item unmapped.
    if (taxEnabled && jurisdiction && form.tax.rateCode) {
      Object.entries(validateTaxFields(taxCtx, form.tax.values)).forEach(([k, v]) => { e[`tax.${k}`] = v; });
    }
    if (Object.keys(e).length) {
      setErrors(e);
      const firstKey = Object.keys(e)[0];
      setTab(tabOfError(firstKey));
      const packErrKeys = Object.keys(e).filter(k => k.endsWith('.weight') || k.endsWith('.size'));
      if (packErrKeys.length) {
        setExpanded(s => { const n = new Set(s); packErrKeys.forEach(k => n.add(k.split('.')[1])); return n; });
      }
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    try {
      const ok = await itemService.isCodeAvailable(form.itemCode.trim(), mode === 'edit' ? loaded?.id : undefined);
      if (!ok) { setErrors({ itemCode: 'This code is already used by another item.' }); setTab('general'); return; }
    } catch { /* the server enforces it on save */ }
    setErrors({});
    setConfirmOpen(true);
  };

  const checkCode = async () => {
    const code = form.itemCode.trim();
    if (!code) return;
    try {
      const ok = await itemService.isCodeAvailable(code, mode === 'edit' ? loaded?.id : undefined);
      if (!ok && formRef.current.itemCode.trim() === code) {
        setErrors(p => ({ ...p, itemCode: 'This code is already used by another item.' }));
      }
    } catch { /* checked again on save */ }
  };

  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload = buildRequest(form);
      // Without a jurisdiction (or when it could not be read) there is nothing to map to:
      // omit TAX so the server leaves any saved mapping alone.
      if (taxEnabled && jurisdiction) payload.extensions = { [TAX_EXT]: buildTaxPayload(form.tax) };
      if (mode === 'edit' && loaded) {
        await itemService.update(loaded.id, { ...payload, rowVersion: loaded.rowVersion });
      } else {
        await itemService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(`Item "${payload.itemName}" ${mode === 'edit' ? 'updated' : 'created'}.`);
      onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      const { status, message } = apiError(err);
      if (status === 409 && mode === 'edit') {
        notificationService.toast.error(message || 'This item was changed by someone else after you opened it. Close it and open it again to get the latest version.', 8000);
      } else {
        notificationService.toast.error(message || 'Could not save the item.');
      }
    }
  };

  if (!isOpen) return null;

  const errOf = (k: string) => errors[k];
  const errCls = (k: string, base = 'form-input') => `${base}${errors[k] ? ' form-field-error' : ''}`;
  const errKeys = Object.keys(errors);
  const tabHasError = (id: TabId) => errKeys.some(k => tabOfError(k) === id);
  // Tax is a real tab in every mode; other module codes keep the read-only placeholder (edit only).
  const extensionTabs = extensions.filter(code => code === TAX_EXT || mode === 'edit');
  const tabs = [
    { id: 'general', label: 'General', hasError: tabHasError('general') },
    { id: 'packs', label: 'Packs & barcodes', hasError: tabHasError('packs') },
    { id: 'class', label: 'Classification', hasError: tabHasError('class') },
    { id: 'stock', label: 'Stock & tracking', hasError: tabHasError('stock') },
    ...extensionTabs.map(code => (code === TAX_EXT
      ? { id: `ext:${code}`, label: 'Tax', hasError: tabHasError(`ext:${code}`) }
      : { id: `ext:${code}`, label: code })),
  ];
  const headerText = mode === 'edit'
    ? `Edit Item${loaded ? ` — ${loaded.itemCode}` : ''}`
    : mode === 'copy' ? 'Copy Item' : 'Add Item';
  const disabled = saving || loadingData;

  // ---- Tab: General ------------------------------------------------------
  const renderGeneral = () => (
    <>
      <div className="form-grid-2">
        <div className="form-group" data-field="itemCode">
          <label className="form-label">Item code <span className="text-danger">*</span></label>
          <input className={errCls('itemCode')} value={form.itemCode} maxLength={40}
            onChange={e => { clearErr('itemCode'); upd({ itemCode: e.target.value }); }}
            onBlur={checkCode} />
          {errOf('itemCode') && <div className="form-error-message">{errOf('itemCode')}</div>}
        </div>
        <div className="form-group" data-field="itemName">
          <label className="form-label">Item name <span className="text-danger">*</span></label>
          <input className={errCls('itemName')} value={form.itemName} maxLength={250}
            onChange={e => { clearErr('itemName'); upd({ itemName: e.target.value }); }} />
          {errOf('itemName') && <div className="form-error-message">{errOf('itemName')}</div>}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-textarea" rows={2} value={form.description}
          onChange={e => upd({ description: e.target.value })} />
      </div>
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Item type</label>
          <div style={{ display: 'flex', gap: '1.25rem', paddingTop: '0.35rem' }}>
            {(['STOCK', 'NON_STOCK'] as ItemType[]).map(t => (
              <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input type="radio" name="itemType" checked={form.itemType === t}
                  onChange={() => { upd({ itemType: t }); if (t === 'NON_STOCK') clearErr(...errKeys.filter(k => k.startsWith('class.'))); }} />
                {TYPE_LABEL[t]}
              </label>
            ))}
          </div>
          {form.itemType === 'NON_STOCK' && <div style={hintStyle}>Bought and sold, never counted as stock</div>}
        </div>
        <div className="form-group">
          <SyutSelect label="Category" isAsync loadOptions={loadCategories} defaultOptions cacheOptions={false}
            seedOptions={form.categoryId
              ? [{ value: form.categoryId, label: categoryLabels.current.get(form.categoryId) || '' }]
              : undefined}
            menuPortalTarget={document.body} value={form.categoryId}
            onChange={v => upd({ categoryId: (v as string) || '' })}
            placeholder="None" isClearable />
        </div>
        <div className="form-group" data-field="baseUomId">
          <SyutSelect label={<>Base unit <span className="text-danger">*</span></>}
            options={uomOptions} value={form.baseUomId}
            onChange={v => { clearErr('baseUomId', `pack.${BASE_KEY}.uom`); upd({ baseUomId: (v as string) || '' }); }}
            menuPortalTarget={document.body} placeholder="Select unit"
            error={errOf('baseUomId') || errOf(`pack.${BASE_KEY}.uom`)}
            helperText="The smallest unit you ever buy, sell or count. Stock is held in it." />
        </div>
        <div className="form-group">
          <SyutSelect label="Status" options={STATUS_OPTIONS} value={form.statusCode}
            onChange={v => upd({ statusCode: (v as string) || 'ACTIVE' })}
            isClearable={false} menuPortalTarget={document.body} />
        </div>
      </div>
      <div className="form-group">
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={form.blockedForPurchase} onChange={e => upd({ blockedForPurchase: e.target.checked })} />
            Blocked for purchase
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={form.blockedForSale} onChange={e => upd({ blockedForSale: e.target.checked })} />
            Blocked for sale
          </label>
        </div>
        <div style={hintStyle}>Discontinued items are usually blocked for purchase but still sellable</div>
      </div>
    </>
  );

  // ---- Tab: Packs & barcodes --------------------------------------------
  const renderPackDetails = (p: PackRow) => {
    const wErr = errOf(`pack.${p.key}.weight`);
    const sErr = errOf(`pack.${p.key}.size`);
    const num = (field: keyof PackRow, label: string, errKey: string) => (
      <div style={{ minWidth: 90, flex: 1 }}>
        <label className="form-label" style={{ fontSize: '0.75rem' }}>{label}</label>
        <input type="number" min={0} step="any" className={errCls(errKey)} style={cellInput} value={p[field] as string}
          onChange={e => { clearErr(errKey); updPack(p.key, { [field]: e.target.value }); }} />
      </div>
    );
    return (
      <tr>
        <td style={{ ...tdStyle, background: 'var(--bg-secondary)' }} colSpan={6}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }} data-field={`pack.${p.key}.weight`}>
            {num('netWeight', 'Net weight', `pack.${p.key}.weight`)}
            {num('grossWeight', 'Gross weight', `pack.${p.key}.weight`)}
            <div style={{ minWidth: 170, flex: 1.5 }}>
              <SyutSelect label={<span style={{ fontSize: '0.75rem' }}>Weight unit</span>} size="sm" options={weightOptions}
                value={p.weightUomId} isClearable menuPortalTarget={document.body} placeholder="Unit"
                onChange={v => { clearErr(`pack.${p.key}.weight`); updPack(p.key, { weightUomId: (v as string) || '' }); }} />
            </div>
          </div>
          {wErr && <div className="form-error-message">{wErr}</div>}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginTop: '0.5rem' }} data-field={`pack.${p.key}.size`}>
            {num('length', 'Length', `pack.${p.key}.size`)}
            {num('width', 'Width', `pack.${p.key}.size`)}
            {num('height', 'Height', `pack.${p.key}.size`)}
            <div style={{ minWidth: 170, flex: 1.5 }}>
              <SyutSelect label={<span style={{ fontSize: '0.75rem' }}>Size unit</span>} size="sm" options={lengthOptions}
                value={p.dimensionUomId} isClearable menuPortalTarget={document.body} placeholder="Unit"
                onChange={v => { clearErr(`pack.${p.key}.size`); updPack(p.key, { dimensionUomId: (v as string) || '' }); }} />
            </div>
          </div>
          {sErr && <div className="form-error-message">{sErr}</div>}
        </td>
      </tr>
    );
  };

  const renderPacks = () => {
    const usedUoms = new Set(form.packs.map(p => packUomId(form, p)).filter(Boolean));
    return (
      <>
        <div style={sectionTitle}>Packs</div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '24%' }}>Unit</th>
              <th style={thStyle}>Contains</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Default for purchase</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Default for sales</th>
              <th style={thStyle}>Weight &amp; size</th>
              <th style={{ ...thStyle, width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {form.packs.map(p => {
              const isBase = p.key === BASE_KEY;
              const uid = packUomId(form, p);
              const code = uomById.get(uid)?.uomCode;
              const opts = uomOptions.filter(o => o.value === p.uomId || !usedUoms.has(o.value));
              const detailErr = !!(errOf(`pack.${p.key}.weight`) || errOf(`pack.${p.key}.size`));
              const summary = [
                hasWeight(p) ? 'weight' : '',
                hasSize(p) ? 'size' : '',
              ].filter(Boolean).join(' & ');
              return (
                <React.Fragment key={p.key}>
                  <tr>
                    <td style={tdStyle} data-field={`pack.${p.key}.uom`}>
                      {isBase ? (
                        <div style={{ padding: '0.3rem 0' }}>
                          <strong>{code || '—'}</strong>{' '}
                          <span className="badge" style={mutedBadge}>Base unit</span>
                        </div>
                      ) : (
                        <SyutSelect size="sm" options={opts} value={p.uomId} menuPortalTarget={document.body}
                          placeholder="Unit" isClearable={false} error={errOf(`pack.${p.key}.uom`)}
                          onChange={v => { clearErr(`pack.${p.key}.uom`); updPack(p.key, { uomId: (v as string) || '' }); }} />
                      )}
                      {isBase && errOf(`pack.${p.key}.uom`) && <div className="form-error-message">{errOf(`pack.${p.key}.uom`)}</div>}
                    </td>
                    <td style={tdStyle} data-field={`pack.${p.key}.factor`}>
                      {isBase ? (
                        <div style={{ padding: '0.3rem 0', color: 'var(--text-secondary)' }}>1 {code || 'unit'} = 1 {baseCode}</div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
                            <span>1 {code || 'pack'} =</span>
                            <input type="number" min={0} step="any" className={errCls(`pack.${p.key}.factor`)}
                              style={{ ...cellInput, width: 110 }} value={p.factor}
                              onChange={e => { clearErr(`pack.${p.key}.factor`); updPack(p.key, { factor: e.target.value }); }} />
                            <span>{baseCode}</span>
                          </div>
                          {errOf(`pack.${p.key}.factor`) && <div className="form-error-message">{errOf(`pack.${p.key}.factor`)}</div>}
                        </>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="radio" name="purchaseDefault" checked={p.isPurchaseDefault} readOnly
                        onClick={() => setDefault(p.key, 'isPurchaseDefault')} title="Click again to clear" />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="radio" name="salesDefault" checked={p.isSalesDefault} readOnly
                        onClick={() => setDefault(p.key, 'isSalesDefault')} title="Click again to clear" />
                    </td>
                    <td style={tdStyle}>
                      <button type="button" className="btn btn-link btn-sm" onClick={() => toggleExpanded(p.key)}
                        style={detailErr ? { color: 'var(--danger, #ef4444)' } : undefined}>
                        {expanded.has(p.key) ? <IconSystem.chevronUp size={14} /> : <IconSystem.chevronDown size={14} />}
                        {' '}{summary ? `Has ${summary}` : 'Add'}
                      </button>
                    </td>
                    <td style={tdStyle}>
                      {!isBase && (
                        <button type="button" className="btn btn-link btn-sm" title="Remove pack" onClick={() => removePack(p.key)}>
                          <IconSystem.trash size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded.has(p.key) && renderPackDetails(p)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: '0.5rem' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addPack} disabled={disabled}>
            <IconSystem.add size={14} /> Add pack
          </button>
        </div>

        <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Barcodes &amp; identifiers</div>
        {form.identifiers.length === 0 ? (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
            No barcodes or codes yet. Scanning a barcode finds the item and the pack it belongs to.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '20%' }}>Type</th>
                <th style={thStyle}>Value</th>
                <th style={{ ...thStyle, width: '22%' }}>Pack</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Primary</th>
                <th style={{ ...thStyle, width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {form.identifiers.map(r => {
                const msg = errOf(`ident.${r.key}`) || liveIdentIssues[r.key];
                return (
                  <tr key={r.key}>
                    <td style={tdStyle}>
                      <SyutSelect size="sm" options={IDENTIFIER_OPTIONS} value={r.identifierType} isClearable={false}
                        menuPortalTarget={document.body}
                        onChange={v => updIdent(r.key, { identifierType: ((v as string) || 'EAN') as IdentifierType })} />
                    </td>
                    <td style={tdStyle} data-field={`ident.${r.key}`}>
                      <input className={`form-input${msg ? ' form-field-error' : ''}`} style={cellInput}
                        value={r.identifierValue} maxLength={100}
                        inputMode={isBarcodeType(r.identifierType) ? 'numeric' : undefined}
                        onChange={e => updIdent(r.key, { identifierValue: e.target.value })} />
                      {msg && <div className="form-error-message">{msg}</div>}
                    </td>
                    <td style={tdStyle}>
                      <SyutSelect size="sm" options={packOptions} value={r.packKey} isClearable={false}
                        menuPortalTarget={document.body}
                        onChange={v => updIdent(r.key, { packKey: (v as string) || BASE_KEY })} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={r.isPrimary} onChange={e => updIdent(r.key, { isPrimary: e.target.checked })} />
                    </td>
                    <td style={tdStyle}>
                      <button type="button" className="btn btn-link btn-sm" title="Remove" onClick={() => removeIdent(r.key)}>
                        <IconSystem.trash size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: '0.5rem' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addIdent} disabled={disabled}>
            <IconSystem.add size={14} /> Add identifier
          </button>
        </div>
        <div style={hintStyle}>A barcode already held by another item is refused when you save.</div>
      </>
    );
  };

  // ---- Tab: Classification ----------------------------------------------
  const renderClassification = () => (
    dims.length === 0 ? (
      <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
        No classification dimensions are active. Set them up under Inventory → Classifications.
      </div>
    ) : (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '32%' }}>Dimension</th>
            <th style={thStyle}>Value</th>
            <th style={{ ...thStyle, width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {dims.map(d => {
            const value = form.classifications[d.id] || '';
            const required = d.isRequired && form.itemType === 'STOCK';
            const parentDimId = parentDimOf(d);
            const parentValueId = parentDimId ? form.classifications[parentDimId] || '' : '';
            const waitingForParent = !!parentDimId && !parentValueId;
            const note = classNotes[d.id];
            return (
              <tr key={d.id}>
                <td style={tdStyle}>
                  <div style={{ padding: '0.3rem 0', fontWeight: 500 }}>
                    {d.dimensionName}{' '}
                    {required && <span className="badge" style={dangerBadge}>Required</span>}
                  </div>
                  {d.description && <div style={{ ...hintStyle, marginTop: 0 }}>{d.description}</div>}
                </td>
                <td style={tdStyle} data-field={`class.${d.id}`}>
                  <SyutSelect key={`${d.id}:${parentValueId}`} size="sm" isAsync loadOptions={valueLoaders[d.id]}
                    defaultOptions cacheOptions={false}
                    seedOptions={value ? [{ value, label: valueLabels.current.get(value) || '' }] : undefined}
                    value={value} menuPortalTarget={document.body} placeholder="Not set" isClearable={false}
                    isDisabled={waitingForParent}
                    error={errOf(`class.${d.id}`)}
                    onChange={v => {
                      clearErr(`class.${d.id}`);
                      setClassValue(d.id, (v as string) || '');
                    }} />
                  {waitingForParent && (
                    <div style={hintStyle}>Choose {d.dependsOnDimensionName || 'the parent classification'} first</div>
                  )}
                  {note && !value && (
                    <div style={{ ...hintStyle, color: 'var(--warning-text, #b45309)' }}>{note}</div>
                  )}
                </td>
                <td style={tdStyle}>
                  {value && (
                    <button type="button" className="btn btn-link btn-sm" title="Clear"
                      onClick={() => setClassValue(d.id, '')}>
                      <IconSystem.x size={15} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )
  );

  // ---- Tab: Stock & tracking --------------------------------------------
  const numberField = (field: 'shelfLifeDays' | 'reorderLevel' | 'minStock' | 'maxStock', label: string, step = 'any') => (
    <div className="form-group" data-field={field}>
      <label className="form-label">{label}</label>
      <input type="number" min={0} step={step} className={errCls(field)} value={form[field]}
        onChange={e => { clearErr(field); upd({ [field]: e.target.value }); }} />
      {errOf(field) && <div className="form-error-message">{errOf(field)}</div>}
    </div>
  );

  const renderStock = () => (
    <>
      <div style={sectionTitle}>Tracking</div>
      <div className="form-grid-3">
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.875rem', paddingTop: '1.6rem' }}>
            <input type="checkbox" checked={form.trackBatch}
              onChange={e => upd(e.target.checked ? { trackBatch: true } : { trackBatch: false, trackExpiry: false })} />
            Track batches
          </label>
        </div>
        <div className="form-group">
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', paddingTop: '1.6rem',
            cursor: form.trackBatch ? 'pointer' : 'not-allowed', color: form.trackBatch ? undefined : 'var(--text-muted)',
          }}>
            <input type="checkbox" checked={form.trackExpiry} disabled={!form.trackBatch}
              onChange={e => upd({ trackExpiry: e.target.checked })} />
            Track expiry
          </label>
          {!form.trackBatch && <div style={hintStyle}>Expiry is tracked per batch — turn on batches first</div>}
        </div>
        {numberField('shelfLifeDays', 'Shelf life (days)', '1')}
      </div>
      <div style={hintStyle}>Serial numbers: coming later.</div>

      <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>
        This entity{selectedEntity?.name ? ` · ${selectedEntity.name}` : ''}
      </div>
      <div className="form-grid-2">
        <div className="form-group">
          <SyutSelect label="Default location" isAsync loadOptions={loadLocations} defaultOptions cacheOptions={false}
            seedOptions={form.defaultStockLocationId
              ? [{ value: form.defaultStockLocationId, label: locationLabels.current.get(form.defaultStockLocationId) || '' }]
              : undefined}
            menuPortalTarget={document.body} value={form.defaultStockLocationId}
            onChange={v => upd({ defaultStockLocationId: (v as string) || '' })}
            placeholder="None" isClearable />
        </div>
      </div>
      <div className="form-grid-3">
        {numberField('reorderLevel', `Reorder level (${baseCode})`)}
        {numberField('minStock', `Minimum (${baseCode})`)}
        {numberField('maxStock', `Maximum (${baseCode})`)}
      </div>
      <div style={hintStyle}>These settings apply to the entity selected in the header only; other entities keep their own.</div>
    </>
  );

  // ---- Tab: Tax (extension TAX) -----------------------------------------
  const renderTax = () => {
    if (!taxSetup) return null; // still loading (the spinner covers the modal)
    if (!taxCtx) {
      return (
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
          This entity's tax settings could not be read. The item saves without changing its tax mapping — reopen it to try again.
        </div>
      );
    }
    if (!jurisdiction) {
      return (
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
          {selectedEntity?.name || 'This entity'} has no tax jurisdiction yet. Set the jurisdiction under Tax Settings first —
          until then the item saves without a tax mapping.
        </div>
      );
    }
    const t = form.tax;
    const defaultMaster = taxSetup.defaults?.defaultRateCode
      ? (taxSetup.defaults.defaultRateName || taxSetup.defaults.defaultRateCode)
      : null;
    const savedMapping = t.rowVersion > 0;
    const taxErrors: Record<string, string> = {};
    Object.entries(errors).forEach(([k, v]) => { if (k.startsWith('tax.')) taxErrors[k.slice(4)] = v; });
    return (
      <>
        <div style={sectionTitle}>
          Tax · {jurisdiction.jurisdictionName}{selectedEntity?.name ? ` · ${selectedEntity.name}` : ''}
        </div>
        <div className="form-group" data-field="tax.rateCode">
          <label className="form-label">Tax master</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="form-input" style={{ flex: 1, minHeight: 38, display: 'flex', alignItems: 'center', background: 'var(--bg-primary)' }}>
              {t.rateCode
                ? <span><strong>{t.rateName || t.rateCode}</strong> <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem' }}>{t.rateCode}</span></span>
                : <span style={{ color: 'var(--text-muted)' }}>Unmapped — choose a master</span>}
            </div>
            <button type="button" className="btn btn-secondary btn-sm" disabled={disabled} onClick={() => setTaxPickerOpen(true)}>Choose…</button>
            {t.rateCode && !savedMapping && (
              <button type="button" className="btn btn-link btn-sm" disabled={disabled}
                onClick={() => setForm(p => ({ ...p, tax: { ...p.tax, rateCode: '', rateName: '' } }))}>Clear</button>
            )}
          </div>
          {!t.rateCode && (
            <div style={{ ...hintStyle, color: 'var(--warning-text, #b45309)' }}>
              {defaultMaster
                ? 'Not mapped for this entity yet — choose a master, or the default master applies on save.'
                : 'Not mapped for this entity yet — choose a master; posting refuses unmapped items.'}
            </div>
          )}
          <div style={hintStyle}>
            Every document line for this item is priced by this master. A non-taxable item is pinned to an exempt master.
            {defaultMaster && <> Entity default: <strong>{defaultMaster}</strong>.</>}
            {savedMapping && ' To change the saved mapping, choose another master.'}
          </div>
        </div>
        {classificationField && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.8125rem', margin: '0.25rem 0 0.5rem' }}>
            <input type="checkbox" checked={!goodsOnly} onChange={e => setGoodsOnly(!e.target.checked)} disabled={disabled} />
            Show all {classificationField.label} codes (default: goods codes only)
          </label>
        )}
        {/* Keyed by the filter so the classification picker re-runs its default search. */}
        <TaxFieldsPanel key={goodsOnly ? 'goods' : 'all'} context={taxCtx} values={t.values} bare disabled={disabled}
          errors={taxErrors} asyncSources={asyncSources} onChange={updTaxValue} />
      </>
    );
  };

  const renderExtension = (code: string) => {
    const payload = loaded?.extensions?.[code];
    return (
      <>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Managed by the {code} module</div>
        <pre style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem',
          padding: '0.75rem', fontSize: '0.78rem', color: 'var(--text-primary)', overflow: 'auto', margin: 0,
        }}>
          {payload === undefined ? '—' : JSON.stringify(payload, null, 2)}
        </pre>
      </>
    );
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxWidth: 1100, width: '95vw', height: '86vh', display: 'flex', flexDirection: 'column' }}>
        {(saving || loadingData) && <Spinner label={saving ? 'Saving…' : 'Loading…'} />}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{headerText}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={disabled}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
        <div className="modal-content" style={{ flex: 1, minHeight: 0, overflow: 'auto', color: 'var(--text-primary)' }}>
          {tab === 'general' && renderGeneral()}
          {tab === 'packs' && renderPacks()}
          {tab === 'class' && renderClassification()}
          {tab === 'stock' && renderStock()}
          {tab === `ext:${TAX_EXT}` && renderTax()}
          {tab.startsWith('ext:') && tab !== `ext:${TAX_EXT}` && renderExtension(tab.slice(4))}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={disabled}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
      <ConfirmDialog open={confirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} item "${form.itemName.trim()}"?${taxEnabled && jurisdiction && form.tax.rateCode ? `

Tax master: ${form.tax.rateName || form.tax.rateCode}.` : ''}`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setConfirmOpen(false)} />
      {taxEnabled && jurisdiction && (
        <TaxMasterPicker
          isOpen={taxPickerOpen}
          title="Choose the tax master for this item"
          recentlyUsedKey="item-master.tax-master"
          load={(q) => taxMasterService.list(jurisdiction.jurisdictionCode, q)}
          onSelect={(m) => {
            clearErr('tax.rateCode');
            setForm(p => ({ ...p, tax: { ...p.tax, rateCode: m.rateCode, rateName: m.name } }));
            setTaxPickerOpen(false);
          }}
          onClose={() => setTaxPickerOpen(false)}
        />
      )}
      <ConfirmDialog open={discardOpen} title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?" confirmLabel="Discard"
        cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)} />
    </>
  );
};

// ============================================================================
// VIEW MODAL
// ============================================================================

const ViewField: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface ItemViewModalProps {
  isOpen: boolean;
  item: ItemDto | null;
  onClose: () => void;
  onEdit?: (item: ItemDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (item: ItemDto) => void;
}

const ItemViewModal: React.FC<ItemViewModalProps> = ({
  isOpen, item, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const { selectedEntity } = useAuthContext();
  const [full, setFull] = useState<ItemDto | null>(null);
  const [extensions, setExtensions] = useState<string[]>([]);
  const [taxSetup, setTaxSetup] = useState<TaxSetup | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const entityKey = selectedEntity?.id || '';

  useEffect(() => {
    if (!isOpen || !item) { setFull(null); setLoadingData(false); return; }
    let live = true;
    setLoadingData(true);
    Promise.all([
      itemService.getById(item.id).catch(() => null),
      loadEnabledExtensions(),
      loadTaxSetup(entityKey),
    ]).then(([detail, ext, setup]) => {
      if (!live) return;
      if (!detail) notificationService.toast.error('Could not load the item details.');
      setFull(detail); setExtensions(ext); setTaxSetup(setup);
    }).finally(() => { if (live) setLoadingData(false); });
    return () => { live = false; };
  }, [isOpen, item, entityKey]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !item) return;
    minimize({
      id: `${tabId}-view-modal-${item.itemCode}`,
      title: `View - ${item.itemCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(item),
    });
    onClose();
  }, [tabId, moduleName, item, minimize, onClose, onRestoreView]);

  if (!isOpen || !item) return null;
  const it = full ?? item;
  const packs = [...(full?.uoms || [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const baseCode = it.baseUomCode || 'base unit';
  const es = full?.entitySetting;
  const fmt = (n?: number | null) => (n == null ? '' : String(n));
  const weightText = (p: (typeof packs)[number]) => {
    if (p.netWeight == null && p.grossWeight == null) return '—';
    return `${fmt(p.netWeight) || '—'} net / ${fmt(p.grossWeight) || '—'} gross ${p.weightUomCode || ''}`.trim();
  };
  const sizeText = (p: (typeof packs)[number]) => {
    if (p.length == null && p.width == null && p.height == null) return '—';
    return `${fmt(p.length) || '—'} × ${fmt(p.width) || '—'} × ${fmt(p.height) || '—'} ${p.dimensionUomCode || ''}`.trim();
  };
  const blocked = [it.blockedForPurchase ? 'Purchase' : '', it.blockedForSale ? 'Sale' : ''].filter(Boolean).join(', ');

  // Tax (extension TAX): read-only master + the jurisdiction's fields, as the Service view shows them.
  const renderTaxView = () => {
    const ctx = taxSetup?.ctx ?? null;
    const jur = ctx?.jurisdiction ?? null;
    const title = <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Tax{jur ? ` · ${jur.jurisdictionName}` : ''}</div>;
    if (!ctx || !jur) {
      return (
        <>
          {title}
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {!taxSetup ? '—' : !ctx
              ? "This entity's tax settings could not be read."
              : `${selectedEntity?.name || 'This entity'} has no tax jurisdiction yet — set it under Tax Settings first.`}
          </div>
        </>
      );
    }
    const m = asMapping(full?.extensions?.[TAX_EXT]);
    const hasDefault = !!taxSetup?.defaults?.defaultRateCode;
    const classificationField = ctx.fields.find(f => bindingOf(f) === CLASSIFICATION_BINDING);
    const unitField = ctx.fields.find(f => bindingOf(f) === UNIT_BINDING);
    const otherFields = ctx.fields.filter(f => bindingOf(f) !== CLASSIFICATION_BINDING && bindingOf(f) !== UNIT_BINDING);
    const unitName = m?.unitCode ? (ctx.units || []).find(u => u.unitCode === m.unitCode)?.unitName : undefined;
    return (
      <>
        {title}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <ViewField label="Tax master" fullWidth value={m?.rateCode
            ? `${m.rateName || m.rateCode} (${m.rateCode})`
            : (
              <span>
                <span className="badge" style={dangerBadge}>Unmapped</span>{' '}
                <span style={{ color: 'var(--text-muted)' }}>
                  {hasDefault
                    ? 'Not mapped for this entity yet — the default master applies on the next save.'
                    : 'Not mapped for this entity yet — posting refuses unmapped items.'}
                </span>
              </span>
            )} />
          {classificationField && (
            <ViewField label={classificationField.label}
              value={m?.classificationCode ? (m.classificationDescription ? `${m.classificationCode} — ${m.classificationDescription}` : m.classificationCode) : null} />
          )}
          {unitField && (
            <ViewField label={unitField.label} value={m?.unitCode ? (unitName ? `${m.unitCode} — ${unitName}` : m.unitCode) : null} />
          )}
          {otherFields.map(f => (
            <ViewField key={f.fieldKey} label={f.label}
              value={m?.attributes?.[f.fieldKey] != null ? String(m.attributes[f.fieldKey]) : null} />
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxWidth: 1100, width: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        {loadingData && <Spinner label="Loading…" />}
        <div className="modal-header">
          <h2 className="modal-title">Item Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={loadingData}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ flex: 1, minHeight: 0, overflow: 'auto', color: 'var(--text-primary)' }}>
          <div style={sectionTitle}>General</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Item code" value={it.itemCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge" style={it.statusColor ? { background: it.statusColor, color: '#fff' } : undefined}>
                  {it.statusName || it.statusCode || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Item name" value={it.itemName} fullWidth />
            <ViewField label="Description" value={it.description} fullWidth />
            <ViewField label="Item type" value={TYPE_LABEL[it.itemType]} />
            <ViewField label="Category" value={it.categoryName} />
            <ViewField label="Base unit" value={it.baseUomCode ? `${it.baseUomCode}${it.baseUomName ? ` — ${it.baseUomName}` : ''}` : null} />
            <ViewField label="Blocked for" value={blocked || 'Not blocked'} />
          </div>

          <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Packs</div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Unit</th>
                <th style={thStyle}>Contains</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Purchase default</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Sales default</th>
                <th style={thStyle}>Weight</th>
                <th style={thStyle}>Size (L × W × H)</th>
              </tr>
            </thead>
            <tbody>
              {packs.length === 0 && (
                <tr><td style={{ ...tdStyle, color: 'var(--text-muted)' }} colSpan={6}>—</td></tr>
              )}
              {packs.map(p => (
                <tr key={p.id}>
                  <td style={tdStyle}>
                    <strong>{p.uomCode || '—'}</strong>{' '}
                    {p.isBase && <span className="badge" style={mutedBadge}>Base unit</span>}
                  </td>
                  <td style={tdStyle}>1 {p.uomCode} = {p.factorToBase} {baseCode}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{p.isPurchaseDefault ? <IconSystem.check size={15} /> : ''}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{p.isSalesDefault ? <IconSystem.check size={15} /> : ''}</td>
                  <td style={tdStyle}>{weightText(p)}</td>
                  <td style={tdStyle}>{sizeText(p)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Barcodes &amp; identifiers</div>
          {(full?.identifiers || []).length === 0 ? (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>None</div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Value</th>
                  <th style={thStyle}>Pack</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Primary</th>
                </tr>
              </thead>
              <tbody>
                {(full?.identifiers || []).map(i => (
                  <tr key={i.id}>
                    <td style={tdStyle}>{identifierLabel(i.identifierType)}</td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono, monospace)' }}>{i.identifierValue}</td>
                    <td style={tdStyle}>{i.uomCode || baseCode}{!i.uomId || i.uomId === it.baseUomId ? ' (base)' : ''}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{i.isPrimary ? <IconSystem.check size={15} /> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Classification</div>
          {(full?.classifications || []).length === 0 ? (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>None</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {(full?.classifications || []).map(c => (
                <ViewField key={c.dimensionId} label={c.dimensionName || c.dimensionCode || 'Dimension'} value={c.valueName || c.valueCode} />
              ))}
            </div>
          )}

          <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>Stock &amp; tracking</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
            <ViewField label="Track batches" value={it.trackBatch ? 'Yes' : 'No'} />
            <ViewField label="Track expiry" value={it.trackExpiry ? 'Yes' : 'No'} />
            <ViewField label="Shelf life (days)" value={fmt(it.shelfLifeDays)} />
          </div>
          <div style={{ ...sectionTitle, marginTop: '1rem' }}>This entity{selectedEntity?.name ? ` · ${selectedEntity.name}` : ''}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Default location" fullWidth
              value={es?.defaultStockLocationCode ? `${es.defaultStockLocationCode} — ${es.defaultStockLocationName || ''}` : es?.defaultStockLocationName} />
            <ViewField label={`Reorder level (${baseCode})`} value={fmt(es?.reorderLevel)} />
            <ViewField label={`Minimum (${baseCode})`} value={fmt(es?.minStock)} />
            <ViewField label={`Maximum (${baseCode})`} value={fmt(es?.maxStock)} />
          </div>

          {extensions.includes(TAX_EXT) && renderTaxView()}

          {extensions.filter(code => code !== TAX_EXT).map(code => (
            <React.Fragment key={code}>
              <div style={{ ...sectionTitle, marginTop: '1.25rem' }}>{code}</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Managed by the {code} module</div>
              <pre style={{
                background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.375rem',
                padding: '0.75rem', fontSize: '0.78rem', overflow: 'auto', margin: 0,
              }}>
                {full?.extensions?.[code] === undefined ? '—' : JSON.stringify(full.extensions[code], null, 2)}
              </pre>
            </React.Fragment>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.25rem' }}>
            <ViewField label="Created By" value={it.createdByName} />
            <ViewField label="Created At" value={it.createdAt ? new Date(it.createdAt).toLocaleString() : null} />
            {it.updatedAt && (
              <>
                <ViewField label="Updated By" value={it.updatedByName} />
                <ViewField label="Updated At" value={new Date(it.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(item); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// SCREEN
// ============================================================================

type ViewMode = 'list' | 'grid';
type TriState = '' | 'true' | 'false';

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'itemCode', label: 'Code' },
  { id: 'itemName', label: 'Name' },
  { id: 'category', label: 'Category' },
  { id: 'itemType', label: 'Type' },
  { id: 'baseUom', label: 'Base unit' },
  { id: 'packs', label: 'Packs' },
  { id: 'barcode', label: 'Barcode' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

const TRI_OPTIONS: SelectOption[] = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const triToBool = (t: TriState): boolean | undefined => (t === '' ? undefined : t === 'true');

const SORT_OPTIONS: SelectOption[] = [
  { value: '', label: 'Default' },
  { value: 'itemCode', label: 'Code' },
  { value: 'itemName', label: 'Name' },
  { value: 'categoryName', label: 'Category' },
];

const filterLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' };

const BlockedBadges: React.FC<{ item: ItemDto }> = ({ item }) => (
  <span style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap' }}>
    {item.blockedForPurchase && <span className="badge" style={dangerBadge}>Purchase</span>}
    {item.blockedForSale && <span className="badge" style={dangerBadge}>Sale</span>}
    {!item.blockedForPurchase && !item.blockedForSale && <span style={{ color: 'var(--text-muted)' }}>—</span>}
  </span>
);

interface ItemMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ItemMasterScreen: React.FC<ItemMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('item-master');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // Re-read the entity's tax setup on each visit so a change under Tax Settings shows up.
  useEffect(() => { invalidateTaxSetup(); }, []);

  const [queryParams, setQueryParams] = useState<ItemQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useItems(queryParams);
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('item-master-columns', COLUMN_DEFS);

  // ---- Filter panel (applied on Apply) ----
  const [filterOpen, setFilterOpen] = useState(false);
  const [fCategoryId, setFCategoryId] = useState('');
  const [fIncludeSub, setFIncludeSub] = useState(true);
  const [fType, setFType] = useState<'' | ItemType>('');
  const [fDimId, setFDimId] = useState('');
  const [fValueId, setFValueId] = useState('');
  const [fBarcode, setFBarcode] = useState('');
  const [fBlockedSale, setFBlockedSale] = useState<TriState>('');
  const [fBlockedPurchase, setFBlockedPurchase] = useState<TriState>('');
  const [fIncludeInactive, setFIncludeInactive] = useState(false);
  const [fSortBy, setFSortBy] = useState('');
  const [fSortDesc, setFSortDesc] = useState(false);
  const filterCategoryLabels = useRef<Map<string, string>>(new Map());
  const filterDimLabels = useRef<Map<string, string>>(new Map());
  const filterValueLabels = useRef<Map<string, string>>(new Map());

  const loadFilterCategories = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemCategoryService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(c => {
        const label = c.path ?? c.categoryName;
        filterCategoryLabels.current.set(c.id, label);
        return { value: c.id, label };
      });
    } catch {
      return [];
    }
  }, []);

  const loadFilterDims = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const q = input.trim().toLowerCase();
      const list = await classificationService.getActive();
      return list
        .filter(d => !q || d.dimensionName.toLowerCase().includes(q) || d.dimensionCode.toLowerCase().includes(q))
        .map(d => {
          filterDimLabels.current.set(d.id, d.dimensionName);
          return { value: d.id, label: d.dimensionName };
        });
    } catch {
      return [];
    }
  }, []);

  const loadFilterValues = useCallback(async (input: string): Promise<SelectOption[]> => {
    if (!fDimId) return [];
    try {
      const res = await classificationService.getValues({ dimensionId: fDimId, searchTerm: input || undefined, pageSize: 50 });
      return res.data.map(v => {
        const label = classValueLabel(v);
        filterValueLabels.current.set(v.id, label);
        return { value: v.id, label };
      });
    } catch {
      return [];
    }
  }, [fDimId]);

  const applyFilters = () => {
    setQueryParams(p => ({
      page: 1,
      pageSize: p.pageSize,
      searchTerm: p.searchTerm,
      categoryId: fCategoryId || undefined,
      includeSubCategories: fCategoryId ? fIncludeSub : undefined,
      itemType: fType || undefined,
      classificationValueId: fValueId || undefined,
      identifier: fBarcode.trim() || undefined,
      blockedForSale: triToBool(fBlockedSale),
      blockedForPurchase: triToBool(fBlockedPurchase),
      includeInactive: fIncludeInactive || undefined,
      sortBy: fSortBy || undefined,
      sortDescending: fSortBy && fSortDesc ? true : undefined,
    }));
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setFCategoryId(''); setFIncludeSub(true); setFType(''); setFDimId(''); setFValueId(''); setFBarcode('');
    setFBlockedSale(''); setFBlockedPurchase(''); setFIncludeInactive(false); setFSortBy(''); setFSortDesc(false);
    setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
  };

  const appliedCount = [
    queryParams.categoryId, queryParams.itemType, queryParams.classificationValueId, queryParams.identifier,
    queryParams.blockedForSale !== undefined ? '1' : '', queryParams.blockedForPurchase !== undefined ? '1' : '',
    queryParams.includeInactive ? '1' : '', queryParams.sortBy,
  ].filter(Boolean).length;

  // ---- Modals ----
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [selected, setSelected] = useState<ItemDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const [copyInstanceId, setCopyInstanceId] = useState('1');
  const copyInstanceCounter = useRef(0);
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState<ItemDto | null>(null);

  const restoreIfMinimized = useCallback((modalId: string | null) => {
    if (!modalId || !isMinimized(modalId)) return false;
    const modal = getMinimizedModal(modalId);
    dismissMinimized(modalId);
    modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const handleAdd = useCallback(() => {
    if (formOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized, formOpen]);

  const handleEdit = useCallback((r: ItemDto) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-edit-${r.itemCode}` : null)) return;
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, restoreIfMinimized, formOpen]);

  const handleCopy = useCallback((r: ItemDto) => {
    if (formOpen) formModalMinimizeRef.current?.();
    let next = copyInstanceCounter.current + 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-copy-${next}`)) next++; }
    copyInstanceCounter.current = next;
    setCopyInstanceId(String(next));
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, formOpen]);

  const handleView = useCallback((r: ItemDto) => {
    if (restoreIfMinimized(tabId ? `${tabId}-view-modal-${r.itemCode}` : null)) return;
    setViewItem(r); setViewOpen(true);
  }, [tabId, restoreIfMinimized]);

  const handleRestoreView = useCallback((item: ItemDto) => {
    setViewItem(item); setViewOpen(true);
  }, []);

  const handleRestoreModal = useCallback((m: FormMode, instanceId?: string, recordCode?: string) => {
    if (instanceId) {
      if (m === 'copy') setCopyInstanceId(instanceId);
      else if (m === 'create') setCreateInstanceId(instanceId);
    }
    if (recordCode) {
      const rec = data.find(r => r.itemCode === recordCode);
      if (rec) setSelected(rec);
    }
    setFormMode(m); setFormOpen(true);
  }, [data]);

  const handleDelete = useCallback(async (r: ItemDto) => {
    const ok = await confirmDelete({ resourceLabel: `${r.itemCode} — ${r.itemName}` });
    if (!ok) return;
    try {
      await itemService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Item "${r.itemName}" deleted.`);
      refetch();
    } catch (err) {
      // The server refuses while the item is in use — surface its reason.
      const { status, message } = apiError(err);
      notificationService.toast.error(message || 'Could not delete the item.');
      if (status === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<ItemDto>[] = useMemo(() => [
    { id: 'itemCode', header: 'Code', accessorKey: 'itemCode', size: 130 },
    { id: 'itemName', header: 'Name', accessorKey: 'itemName', size: 260 },
    {
      id: 'category', header: 'Category', accessorKey: 'categoryName', size: 170,
      cell: ({ row }) => row.original.categoryName || '—',
    },
    {
      id: 'itemType', header: 'Type', accessorKey: 'itemType', size: 100,
      cell: ({ row }) => (
        <span className="badge" style={row.original.itemType === 'NON_STOCK' ? mutedBadge : undefined}>
          {TYPE_LABEL[row.original.itemType]}
        </span>
      ),
    },
    {
      id: 'baseUom', header: 'Base unit', accessorKey: 'baseUomCode', size: 100,
      cell: ({ row }) => row.original.baseUomCode || '—',
    },
    {
      id: 'packs', header: 'Packs', accessorKey: 'packSummary', size: 180,
      cell: ({ row }) => row.original.packSummary || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      id: 'barcode', header: 'Barcode', accessorKey: 'primaryBarcode', size: 150,
      cell: ({ row }) => row.original.primaryBarcode
        ? <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem' }}>{row.original.primaryBarcode}</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    {
      id: 'blocked', header: 'Blocked', size: 140,
      cell: ({ row }) => <BlockedBadges item={row.original} />,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || r.statusCode || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: ItemDto): ActionMenuItem[] => {
    // Order: View → Copy → Edit → Delete. View is never gated.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    if (can('UPDATE')) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(r), variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Item'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const HeaderIcon = (moduleIcon && ICONS[moduleIcon]) || IconSystem.tag;
  const fSection = (children: React.ReactNode) => <div className="filter-section">{children}</div>;

  return (
    <EnterpriseScreenShell
      title="Items"
      description="Goods the business buys, stocks and sells — packs, barcodes and groupings"
      icon={<HeaderIcon size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code / name / barcode...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              {fSection(
                <>
                  <SyutSelect label="Category" size="sm" isAsync loadOptions={loadFilterCategories} defaultOptions cacheOptions={false}
                    seedOptions={fCategoryId ? [{ value: fCategoryId, label: filterCategoryLabels.current.get(fCategoryId) || '' }] : undefined}
                    value={fCategoryId} onChange={v => setFCategoryId((v as string) || '')}
                    placeholder="All" isClearable menuPortalTarget={document.body} />
                  <label style={{ ...filterLabel, marginTop: '0.35rem' }}>
                    <input type="checkbox" checked={fIncludeSub} disabled={!fCategoryId}
                      onChange={e => setFIncludeSub(e.target.checked)} /> Include sub-categories
                  </label>
                </>,
              )}
              {fSection(
                <SyutSelect label="Type" size="sm" isClearable={false}
                  options={[{ value: '', label: 'All' }, { value: 'STOCK', label: 'Stock' }, { value: 'NON_STOCK', label: 'Non-stock' }]}
                  value={fType} onChange={v => setFType(((v as string) || '') as '' | ItemType)} />,
              )}
              {fSection(
                <>
                  <SyutSelect label="Classification" size="sm" isAsync loadOptions={loadFilterDims} defaultOptions cacheOptions={false}
                    seedOptions={fDimId ? [{ value: fDimId, label: filterDimLabels.current.get(fDimId) || '' }] : undefined}
                    value={fDimId} onChange={v => { setFDimId((v as string) || ''); setFValueId(''); }}
                    placeholder="Choose a dimension" isClearable menuPortalTarget={document.body} />
                  {fDimId && (
                    <div style={{ marginTop: '0.35rem' }}>
                      <SyutSelect key={fDimId} size="sm" isAsync loadOptions={loadFilterValues} defaultOptions cacheOptions={false}
                        seedOptions={fValueId ? [{ value: fValueId, label: filterValueLabels.current.get(fValueId) || '' }] : undefined}
                        value={fValueId} onChange={v => setFValueId((v as string) || '')}
                        placeholder="Choose a value" isClearable menuPortalTarget={document.body} />
                    </div>
                  )}
                </>,
              )}
              {fSection(
                <>
                  <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Barcode (exact)</label>
                  <input type="text" className="form-input" value={fBarcode} onChange={e => setFBarcode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
                    placeholder="Scan or type a code..." style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </>,
              )}
              {fSection(
                <SyutSelect label="Blocked for sale" size="sm" options={TRI_OPTIONS} isClearable={false}
                  value={fBlockedSale} onChange={v => setFBlockedSale(((v as string) || '') as TriState)} />,
              )}
              {fSection(
                <SyutSelect label="Blocked for purchase" size="sm" options={TRI_OPTIONS} isClearable={false}
                  value={fBlockedPurchase} onChange={v => setFBlockedPurchase(((v as string) || '') as TriState)} />,
              )}
              {fSection(
                <>
                  <SyutSelect label="Sort by" size="sm" options={SORT_OPTIONS} isClearable={false}
                    value={fSortBy} onChange={v => setFSortBy((v as string) || '')} />
                  <label style={{ ...filterLabel, marginTop: '0.35rem' }}>
                    <input type="checkbox" checked={fSortDesc} disabled={!fSortBy}
                      onChange={e => setFSortDesc(e.target.checked)} /> Descending
                  </label>
                </>,
              )}
              {fSection(
                <label style={filterLabel}>
                  <input type="checkbox" checked={fIncludeInactive}
                    onChange={e => setFIncludeInactive(e.target.checked)} /> Include inactive
                </label>,
              )}
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection enableSorting={false}
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            frozenLeftColumns={['select', 'itemCode']} frozenRightColumns={['actions']}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No items found.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.itemCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.itemName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span>{TYPE_LABEL[r.itemType]}{r.categoryName ? ` · ${r.categoryName}` : ''}</span>
                    <span>{r.baseUomCode}</span>
                  </div>
                  {r.packSummary && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{r.packSummary}</div>}
                  {r.primaryBarcode && (
                    <div style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-secondary)' }}>{r.primaryBarcode}</div>
                  )}
                  {(r.blockedForPurchase || r.blockedForSale) && <div><BlockedBadges item={r} /></div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ItemViewModal
        isOpen={viewOpen}
        item={viewItem}
        onClose={() => { setViewOpen(false); setViewItem(null); }}
        onEdit={can('UPDATE') ? (it) => { setViewOpen(false); setViewItem(null); handleEdit(it); } : undefined}
        tabId={tabId}
        moduleName={moduleName || 'Items'}
        onRestoreView={handleRestoreView}
      />

      <ItemFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Items'}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default ItemMasterScreen;
